'use es6';

import { List as ImmutableList, Map as ImmutableMap, fromJS } from 'immutable';
import parseModuleId from 'ContentEditorUI/utils/parseModuleId';
import Raven from 'raven-js';
import { omit, pick } from 'underscore';
import { ATTRIBUTES_NOT_NEEDED_INSIDE_PARAMS, NON_BODY_ATTRIBUTES_TO_PICK_FROM_CONTENT_WIDGET } from 'ContentEditorUI/data/moduleTransformHelpers';
import { pathOfAllUnnecessaryWrappersUpToDescendentToKeep } from 'layout-data-lib/CellAndRowsTree/helpers';
import { isLinkedImageModuleId } from 'ContentEditorUI/data/moduleUtils';
import { EditorMetricsTracker } from 'ContentEditorUI/utils/metricsTracker';
import { importTreeFromLayoutDataApi } from 'layout-data-lib/LayoutDataTree/serialize';
import CustomCellTweakingAutoDeleteLogicPerTestFlag from 'ContentEditorUI/data/tree/CustomCellTweakingAutoDeleteLogicPerTestFlag';
import { DND_AREA_ID, parseFlexAreaDataForLayoutTree } from 'ContentEditorUI/utils/email/contentTreeUtils';
import set from 'hs-lodash/set';
import { convertJsonToFieldValueAvroWithoutTypename } from 'layout-data-lib/GridLayoutDataTree/typenameUtils';
const logUnknownModuleToRaven = id => {
  Raven.captureMessage('Attempted to update a module that did not exist', {
    extra: {
      moduleId: id
    }
  });
};
const FLEX_AREA_TREE_NODE_NAME_QUEUE = {};
function createNodeNameQueueForFlexAreaTree() {
  FLEX_AREA_TREE_NODE_NAME_QUEUE.cell = [];
  FLEX_AREA_TREE_NODE_NAME_QUEUE.row = [];
}
function addToFlexAreaNodeNameQueue(type, val) {
  var _FLEX_AREA_TREE_NODE_;
  if (!getIsModuleReducerDiffingEnabled()) {
    return;
  }
  const nodeNames = (_FLEX_AREA_TREE_NODE_ = FLEX_AREA_TREE_NODE_NAME_QUEUE[type]) !== null && _FLEX_AREA_TREE_NODE_ !== void 0 ? _FLEX_AREA_TREE_NODE_ : [];
  nodeNames.push(val);
  FLEX_AREA_TREE_NODE_NAME_QUEUE[type] = nodeNames;
}
export function readFromFlexAreaNodeNameQueue(type) {
  var _FLEX_AREA_TREE_NODE_2;
  if (!getIsModuleReducerDiffingEnabled()) {
    Raven.captureMessage('Trying to add to node name queue without enabling module reducer diffing.');
    console.error('Trying to add to node name queue without enabling module reducer diffing. Make sure to call enableModuleReducerDiffing before the module reducer is called');
  }
  const list = (_FLEX_AREA_TREE_NODE_2 = FLEX_AREA_TREE_NODE_NAME_QUEUE[type]) !== null && _FLEX_AREA_TREE_NODE_2 !== void 0 ? _FLEX_AREA_TREE_NODE_2 : [];
  return list.shift();
}
let isModuleReducerDiffingEnabled = false;
export function enableModuleReducerDiffing() {
  isModuleReducerDiffingEnabled = true;
}
export function getIsModuleReducerDiffingEnabled() {
  return isModuleReducerDiffingEnabled;
}
export const SHARED_ACTION_DATA = {};

// WIDGET CONTAINERS
const getWidgetContainerInfo = (state, id) => {
  let info = {};
  id = parseModuleId(id);
  state.get('widgetContainers').forEach((widgetContainer, widgetContainerKey) => {
    const entry = widgetContainer.get('widgets').findEntry(widget => parseModuleId(widget.get('id')) === id && !widget.get('deleted_at'));
    if (entry) {
      const [order] = entry;
      info = {
        widgetContainerKey,
        order
      };
      return false;
    }
    return true;
  });
  return info;
};
export const getKeyForNonLayoutSection = (state, id) => {
  if (state.hasIn(['widgetsInRichText', id])) {
    return ['widgetsInRichText', id];
  }
  if (state.hasIn(['schemaWidgets', id])) {
    return ['widgets', id];
  }
  const {
    widgetContainerKey,
    order
  } = getWidgetContainerInfo(state, id);
  if (widgetContainerKey === undefined) {
    return undefined;
  }
  return ['widgetContainers', widgetContainerKey, 'widgets', order];
};
const resetOrdersInContainer = (state, containerKey) => {
  const widgetListKey = ['widgetContainers', containerKey, 'widgets'];
  return state.getIn(widgetListKey).reduce((currentState, module, i) => {
    if (i !== module.get('order')) {
      currentState = currentState.setIn([...widgetListKey, i, 'order'], i);
    }
    return currentState;
  }, state);
};
export const addModuleToFlexColumnHelper = (state, columnId, index, module) => {
  let widgetContainer = state.getIn(['widgetContainers', columnId]);
  if (!widgetContainer) {
    widgetContainer = state.getIn(['schemaWidgetContainers', columnId]);
    state = state.setIn(['widgetContainers', columnId], widgetContainer);
    if (!widgetContainer) {
      throw new Error(`Cannot find widget container with given id: ${columnId}`);
    }
  }
  let widgetContainerWidgets = widgetContainer.has('widgets') ? widgetContainer.get('widgets') : new ImmutableList();
  const insertIndex = isNaN(index) ? widgetContainerWidgets.size : index;
  widgetContainerWidgets = widgetContainerWidgets.insert(insertIndex, module);
  state = state.setIn(['widgetContainers', columnId, 'widgets'], widgetContainerWidgets);
  return resetOrdersInContainer(state, columnId);
};
export const removeModule = (state, id) => {
  const {
    widgetContainerKey
  } = getWidgetContainerInfo(state, id);
  if (widgetContainerKey === undefined) {
    // If the module isn't found, no-op for now to not corrupt our state
    Raven.captureMessage('Attempting to delete a module from a flex column where either the flex column does not exist or the module does not exist', {
      extra: {
        moduleId: id
      }
    });
    return state;
  }
  state = state.deleteIn(getKeyForNonLayoutSection(state, id));
  return resetOrdersInContainer(state, widgetContainerKey);
};

// LAYOUT SECTIONS
const prepareNewlyClonedContentTree = (tree, layoutSectionWidgets, {
  isUngatedForMergeInDefaultValues
}) => {
  const mapOfLayoutSectionWidgetsForTree = new ImmutableMap(layoutSectionWidgets.get(tree.getRootName(), new ImmutableList()).map(w => [w.get('name'), w.toJS()]));

  // Step 1) Bring along previous edits that live outside the D&D area (more info in
  // https://git.hubteam.com/HubSpot/ContentEditorUI/pull/4217)
  tree.allModules().map(cell => cell.getName()).forEach(cellName => {
    const layoutSectionWidget = mapOfLayoutSectionWidgetsForTree.get(cellName);

    // If it exists, merge the (cleaned up) body from the layout section widget with the other.
    // non-body attributes (and sigh, more params <-> body pain that we need to someday deal with)
    if (layoutSectionWidget) {
      const newParams = Object.assign({}, pick(layoutSectionWidget, NON_BODY_ATTRIBUTES_TO_PICK_FROM_CONTENT_WIDGET), omit(layoutSectionWidget.body, ATTRIBUTES_NOT_NEEDED_INSIDE_PARAMS));
      ({
        tree
      } = tree.mergeIntoCellParams(cellName, newParams));
    }
  });
  if (isUngatedForMergeInDefaultValues) {
    // Step 2) Find all default image modules and set merge_in_default_values = false on them during
    // copy to content model (more info in https://git.hubteam.com/HubSpot/CMS-Developer-Infrastructure/issues/484)
    tree.allModules().forEach(cell => {
      const isDefaultImageModule = isLinkedImageModuleId(cell.getParams().module_id);
      if (isDefaultImageModule && cell.getValue().merge_in_default_values == null) {
        ({
          tree
        } = tree.mergeIntoCellValue(cell.getName(), {
          merge_in_default_values: false
        }));
      }
    });
  }
  return tree;
};
export const copyLayoutSectionFromSchemaToContent = (state, layoutSectionId, layoutSectionWidgets, {
  isUngatedForMergeInDefaultValues
}) => {
  let clonedTree = state.getIn(['schemaLayoutSectionTrees', layoutSectionId]);
  clonedTree = prepareNewlyClonedContentTree(clonedTree, layoutSectionWidgets, {
    isUngatedForMergeInDefaultValues
  });
  return state.setIn(['layoutSectionTrees', layoutSectionId], clonedTree);
};
export const copyLayoutSectionFromSchemaToContentIfNeeded = (state, layoutSectionId) => {
  if (!state.getIn(['layoutSectionTrees', layoutSectionId])) {
    state = copyLayoutSectionFromSchemaToContent(state, layoutSectionId, state.get('layoutSectionWidgets'), {
      isUngatedForMergeInDefaultValues: state.get('isUngatedForMergeInDefaultValues')
    });
  }
  return state;
};
export const getLayoutSectionTreeToModify = (state, layoutSectionId) => {
  if (!layoutSectionId) {
    return null;
  }
  state = copyLayoutSectionFromSchemaToContentIfNeeded(state, layoutSectionId);
  return state.getIn(['layoutSectionTrees', layoutSectionId]);
};
export const getLayoutSectionTree = (state, layoutSectionId) => {
  const layoutSectionTreePath = ['layoutSectionTrees', layoutSectionId];
  const schemaLayoutSectionTreePath = ['schemaLayoutSectionTrees', layoutSectionId];

  // First look for module in the page's content model layoutSection data.
  // And If not on the page's content model, look for the module in the page's layoutSection schema
  // (e.g. default layout data from the template)
  if (state.hasIn(layoutSectionTreePath)) {
    return state.getIn(layoutSectionTreePath);
  } else if (state.hasIn(schemaLayoutSectionTreePath)) {
    return state.getIn(schemaLayoutSectionTreePath);
  }
  return undefined;
};
export const getGridLayoutTree = (state, gridLayoutId) => {
  const gridlayoutTreePath = ['gridLayoutTrees', gridLayoutId];
  const schemaGridLayoutTreePath = ['schemaGridLayoutTrees', gridLayoutId];

  // First look for module in the page's content model layoutSection data.
  // And If not on the page's content model, look for the module in the page's layoutSection schema
  // (e.g. default layout data from the template)
  if (state.hasIn(gridlayoutTreePath)) {
    return state.getIn(gridlayoutTreePath);
  } else if (state.hasIn(schemaGridLayoutTreePath)) {
    return state.getIn(schemaGridLayoutTreePath);
  }
  return undefined;
};
const getLayoutSectionIds = state => {
  return state.get('schemaLayoutSectionTrees').keySeq().toArray();
};
const getGridLayoutIds = state => {
  return state.get('schemaGridLayoutTrees').keySeq().toArray();
};
export const findTreeWithCellName = (state, id) => {
  for (const layoutSectionId of getLayoutSectionIds(state)) {
    const tree = getLayoutSectionTree(state, layoutSectionId);
    if (tree.hasCell(id) || tree.hasStaticSectionModule(id)) {
      return tree;
    }
  }
  return undefined;
};
export const findGridTreeWithModuleId = (state, id) => {
  for (const gridLayoutId of getGridLayoutIds(state)) {
    const tree = getGridLayoutTree(state, gridLayoutId);
    if (tree.hasNode(id)) {
      return tree;
    }
  }
  return undefined;
};
export const findTreeWithRowName = (state, id) => {
  for (const layoutSectionId of getLayoutSectionIds(state)) {
    const tree = getLayoutSectionTree(state, layoutSectionId);
    if (tree.hasRow(id)) {
      return tree;
    }
  }
  return undefined;
};
const addNewModuleToLayoutSectionWidgets = (state, layoutSectionId, newModuleSchemaJson) => {
  const layoutSectionWidgetsPath = ['layoutSectionWidgets', layoutSectionId];
  state = state.setIn(layoutSectionWidgetsPath, state.getIn(layoutSectionWidgetsPath, new ImmutableList()).push(fromJS(newModuleSchemaJson)));
  return state;
};
const addClonedModuleToLayoutSectionWidgets = (state, layoutSectionId, oldName, newName) => {
  const layoutSectionWidgetsPath = ['layoutSectionWidgets', layoutSectionId];
  const oldLayoutSectionWidget = state.getIn(layoutSectionWidgetsPath).find(m => m.get('name') === oldName);

  // Skip over cloned rows and nested columns that are not in layout_section_widgets
  if (oldLayoutSectionWidget) {
    const newWidget = oldLayoutSectionWidget.merge({
      name: newName
    })
    // Don't copy the old widget/module's order (even though it isn't really used?)
    .delete('order')
    // Don't copy smart-content (for now)
    .delete('definition_id').delete('smart_objects').delete('smart_type').deleteIn(['body', 'hide_module_and_wrapper']);
    state = state.setIn(layoutSectionWidgetsPath, state.getIn(layoutSectionWidgetsPath, new ImmutableList()).push(newWidget));
  }
  return state;
};
const cleanupDeletedCellsFromLayoutSection = (state, deletedColumns, layoutSectionId) => {
  for (const deletedColumn of deletedColumns) {
    // Module groups/wrapper cells are not in layoutSectionWidgets, no need to delete them from there
    if (deletedColumn.isModule()) {
      const layoutSectionWidgetsPath = ['layoutSectionWidgets', layoutSectionId];
      const layoutSectionWidgetsIndex = state.getIn(layoutSectionWidgetsPath).findIndex(m => m.get('name') === deletedColumn.getName());
      if (layoutSectionWidgetsIndex === -1) {
        console.warn(`Error deleting ${deletedColumn.getName()}, it is not in layout section widgets`);
        EditorMetricsTracker.counter('Deleting module missing in schema layout section widgets').increment();
        continue;
      }
      layoutSectionWidgetsPath.push(layoutSectionWidgetsIndex);
      state = state.deleteIn(layoutSectionWidgetsPath);
    }
  }
  return state;
};
const removeUncustomizedWrappersStartingAtColumn = (tree, column) => {
  const {
    descendent
  } = pathOfAllUnnecessaryWrappersUpToDescendentToKeep(column);

  // If there are unnecessary wrappers, move the descendent up to the original column's parent
  // (which will trigger those unncessary wrappers to be automatically deleted)
  if (descendent !== column) {
    if (!descendent.isCell()) {
      throw new Error('Error looking up unnecessary wrappers, descendent should be a column or module');
    }
    const originalParentRowName = column.getParentName();
    return tree.appendColumn(originalParentRowName, {
      existingCellName: descendent.getName()
    });
  }
  return {};
};
const removeWrappersFromSiblingsInSectionIfNeeded = (modifiedTree, deletedColumns) => {
  for (const col of deletedColumns) {
    if (modifiedTree.hasRow(col.getParentName())) {
      const newParentRow = modifiedTree.findRow(col.getParentName());
      const wasColDeletedFromSection = newParentRow.getParent().isRoot();
      const onlyHadOneSibling = newParentRow.getNumberColumns() === 1;

      // If we just deleted column that triggered a section to go from 2 to 1 columns, see if we
      // can remove any unecessary wrapper columns around the last sibling
      if (wasColDeletedFromSection && onlyHadOneSibling) {
        modifiedTree.printTreeWithNames();
        const {
          tree: newTree,
          deletedColumn: moreDeletedColumns
        } = removeUncustomizedWrappersStartingAtColumn(modifiedTree, newParentRow.getColumns()[0]);
        modifiedTree = newTree || modifiedTree;
        deletedColumns = deletedColumns.concat(moreDeletedColumns || []);
        return {
          newTree: modifiedTree,
          deletedColumns
        };
      }
    }
  }
  return {
    newTree: modifiedTree,
    deletedColumns
  };
};
export const removeCellFromLayoutSection = (state, layoutSectionId, cellId, {
  shouldRemoveUnnecessaryWrappersForSingleColumnSection
}) => {
  // If this is the first time the layout section has been touched on the page, copy it to the content model
  state = copyLayoutSectionFromSchemaToContentIfNeeded(state, layoutSectionId);
  const tree = getLayoutSectionTree(state, layoutSectionId);
  const cell = tree.findCell(cellId);
  if (!tree || !cell) {
    throw new Error(`Error deleting ${cellId}, it is not in layout section ${layoutSectionId}`);
  }
  let {
    tree: newTree,
    deletedColumns /* , deletedRows */
  } = tree.removeCell(cellId);
  if (shouldRemoveUnnecessaryWrappersForSingleColumnSection) {
    ({
      newTree,
      deletedColumns
    } = removeWrappersFromSiblingsInSectionIfNeeded(newTree, deletedColumns));
  }
  state = state.setIn(['layoutSectionTrees', layoutSectionId], newTree);

  // Also delete all of the descendent modules from layoutSectionWidgets
  state = cleanupDeletedCellsFromLayoutSection(state, deletedColumns, layoutSectionId);
  return state;
};
export const removeRowFromLayoutSection = (state, layoutSectionId, rowId) => {
  // If this is the first time the layout section has been touched on the page, copy it to the content model
  state = copyLayoutSectionFromSchemaToContentIfNeeded(state, layoutSectionId);
  const tree = getLayoutSectionTree(state, layoutSectionId);
  const row = tree.findRow(rowId);
  if (!tree || !row) {
    throw new Error(`Error deleting ${rowId}, it is not in layout section ${layoutSectionId}`);
  }
  const {
    tree: newTree,
    deletedColumns /* , deletedRows */
  } = tree.removeRow(rowId);
  state = state.setIn(['layoutSectionTrees', layoutSectionId], newTree);

  // Also delete all of the descendent modules from layoutSectionWidgets
  state = cleanupDeletedCellsFromLayoutSection(state, deletedColumns, layoutSectionId);
  return state;
};
const correctLayoutSectionWidgets = (state, originLayoutSectionId, layoutSectionId, modifiedColumns) => {
  const originTreeList = state.getIn(['layoutSectionWidgets', originLayoutSectionId]) || new ImmutableList();
  const treeList = state.getIn(['layoutSectionWidgets', layoutSectionId]) || new ImmutableList();
  const modifiedColumnIds = modifiedColumns.map(column => column.getName());
  const movedWidgets = [];
  modifiedColumnIds.forEach(modifiedColumnId => {
    let movedWidget = originTreeList.find(widget => widget.get('name') === modifiedColumnId);
    if (movedWidget) {
      movedWidget = movedWidget.set('layout_section_id', layoutSectionId);
      movedWidgets.push(movedWidget);
    }
  });
  if (movedWidgets.length) {
    const filteredOriginList = originTreeList.filter(widget => !modifiedColumnIds.includes(widget.get('name')));
    state = state.setIn(['layoutSectionWidgets', originLayoutSectionId], filteredOriginList);
    state = state.setIn(['layoutSectionWidgets', layoutSectionId], treeList.push(...movedWidgets));
  }
  return state;
};
export const updateLayoutSectionTreeAndWidgets = ({
  layoutSectionId,
  originLayoutSectionId,
  newModuleSchemaJson,
  customSectionModuleSchemas,
  modifiedColumns,
  tree,
  originTree,
  mapOfClonedToOldNodeName,
  state
}) => {
  if (newModuleSchemaJson) {
    state = addNewModuleToLayoutSectionWidgets(state, layoutSectionId, newModuleSchemaJson);
  }
  if (customSectionModuleSchemas) {
    customSectionModuleSchemas.forEach(schema => {
      state = addNewModuleToLayoutSectionWidgets(state, layoutSectionId, schema);
    });
  }

  // Make sure that all cloned modules also show up in layout_section_widgets
  if (mapOfClonedToOldNodeName) {
    Object.keys(mapOfClonedToOldNodeName).forEach(newCloneName => {
      const oldName = mapOfClonedToOldNodeName[newCloneName];
      state = addClonedModuleToLayoutSectionWidgets(state, layoutSectionId, oldName, newCloneName);
      // Keep track if its a static section row
      // `mapOfClonedToOldStaticSectionName` is Used in `ModuleDomRenderContainer`
      if (tree.hasRow(oldName) && tree.findRow(oldName).isStaticSection()) {
        const modulesToCopy = tree.findRow(oldName).getModuleChildren();
        const newModules = tree.findRow(newCloneName).getModuleChildren();
        newModules.forEach((moduleToInsert, i) => {
          const moduleToCopy = modulesToCopy[i];
          // Need to add to the layoutSectionWidgets in order for hover selectors to work
          state = addClonedModuleToLayoutSectionWidgets(state, layoutSectionId, moduleToCopy.getName(), moduleToInsert.getName());
        });
        state = state.set('mapOfClonedToOldStaticSectionName',
        // Pass the map to the ModuleDomRenderContainer
        // So we know which static section to clone
        ImmutableMap(mapOfClonedToOldNodeName));
      }
      if (tree.hasCell(oldName)) {
        const treeNode = tree.findCell(oldName);
        if (treeNode && treeNode.isModule()) {
          state = state.set('clonedModulesPendingDomOperations', state.get('clonedModulesPendingDomOperations').add(ImmutableMap({
            originalModule: oldName,
            newModule: newCloneName
          })));
        }
      }

      // Clean up hide_module_and_wrapper flag on the cloned module
      if (tree.hasCell(newCloneName)) {
        var _clonedTreeNode$getPa;
        const clonedTreeNode = tree.findCell(newCloneName);
        if (clonedTreeNode && clonedTreeNode.isModule() && (_clonedTreeNode$getPa = clonedTreeNode.getParams()) !== null && _clonedTreeNode$getPa !== void 0 && _clonedTreeNode$getPa.hide_module_and_wrapper) {
          ({
            tree
          } = tree.mergeIntoCellParams(clonedTreeNode.getName(), {
            hide_module_and_wrapper: false
          }));
        }
      }
    });
  }

  // Don't attempt to update / set the temporary tree from
  // a custom section into redux.
  if (originTree && !customSectionModuleSchemas) {
    state = state.setIn(['layoutSectionTrees', originLayoutSectionId], originTree);
    state = correctLayoutSectionWidgets(state, originLayoutSectionId, layoutSectionId, modifiedColumns);
  }
  return state.setIn(['layoutSectionTrees', layoutSectionId], tree);
};

// MODULES
const updateTinymceUndoDataForModule = (state, id, tinymceUndoData) => {
  return state.mergeIn(['moduleMetaData', id, 'tinymceUndoData'], tinymceUndoData);
};
export const updateMetaDataForModule = (state, id, metaData = {}) => {
  // TODO branden maybe not the best way to do this
  if (metaData.tinymceUndoData) {
    state = updateTinymceUndoDataForModule(state, id, metaData.tinymceUndoData);
    if (getIsModuleReducerDiffingEnabled()) {
      set(SHARED_ACTION_DATA, [id, 'tinymceUndoData'], metaData.tinymceUndoData);
    }
    delete metaData.tinymceUndoData;
  }
  if (metaData.inlineRichTextFieldEditingMetaData) {
    // Copying the pattern set by 'tinymceUndoData'
    // Make sure that we're merging into 'inlineRichTextFieldEditingMetaData'
    // as opposed to overwriting it
    // Then delete `inlineRichTextFieldEditingMetaData` so that the `mergeIn`
    // following this one doesn't overwrite the existing inlineRichTextFieldEditingMetaData
    if (getIsModuleReducerDiffingEnabled()) {
      set(SHARED_ACTION_DATA, [id, 'inlineRichTextFieldEditingMetaData'], metaData.inlineRichTextFieldEditingMetaData);
    }
    state = state.mergeIn(['moduleMetaData', id, 'inlineRichTextFieldEditingMetaData'], metaData.inlineRichTextFieldEditingMetaData);
    delete metaData.inlineRichTextFieldEditingMetaData;
  }
  return state.mergeIn(['moduleMetaData', id], metaData);
};
export const incrementModuleEditVersion = ({
  state,
  id,
  defaultInitialVersion = 1
}) => {
  //The defaultInitialVersion handles a strange edge case where the image needs to be
  //resized on add and the moduleVersion would be set to 1 instead of 0
  //causing us not to be able to find the module on undo/redo
  let newVersion = defaultInitialVersion;
  const editVersionKey = ['moduleMetaData', id, 'editVersion'];
  const currentVersion = state.getIn(editVersionKey);
  if (currentVersion != null) {
    newVersion = currentVersion + 1;
  }
  return state.setIn(editVersionKey, newVersion);
};

// This is used to change UI editable field values like "src" or "text" on a module
export const mergeModuleBodyHelper = (state, id, partialBody, {
  moduleDefaultType
}) => {
  const tree = findTreeWithCellName(state, id);
  const isDefaultImageModule = moduleDefaultType === 'linked_image';
  if (tree) {
    state = copyLayoutSectionFromSchemaToContentIfNeeded(state, tree.getRootName());

    // Make sure to get cell reference in newly modified tree
    let newTree = state.getIn(['layoutSectionTrees', tree.getRootName()]);
    const cell = tree.findCell(id);
    ({
      tree: newTree
    } = newTree.mergeIntoCellParams(cell.getName(), partialBody));
    if (isDefaultImageModule && cell.hasValue() && cell.getValue().merge_in_default_values == null) {
      ({
        tree: newTree
      } = newTree.mergeIntoCellValue(cell.getName(), {
        merge_in_default_values: false
      }));
    }
    return state.setIn(['layoutSectionTrees', tree.getRootName()], newTree);
  }
  const moduleKey = getKeyForNonLayoutSection(state, id);
  if (!moduleKey) {
    logUnknownModuleToRaven(id);
    return state;
  }
  const moduleBodyKey = [...moduleKey, 'body'];
  const modulemergeInDefaultFieldValuesKey = [...moduleKey, 'merge_in_default_values'];
  state = state.mergeIn(moduleBodyKey, fromJS(partialBody));
  if (isDefaultImageModule && !state.hasIn(modulemergeInDefaultFieldValuesKey)) {
    state = state.setIn(modulemergeInDefaultFieldValuesKey, false);
  }
  return state;
};
const injectPathIntoFieldOverridesIfMissing = (state, treeId, id, fieldOverrides) => {
  const modules = state.getIn(['gridLayoutModules', treeId]);
  const module = modules.find(_module => _module.get('name') === id);
  if (module) {
    var _module$get;
    fieldOverrides.path = (_module$get = module.get('body')) === null || _module$get === void 0 ? void 0 : _module$get.get('path');
  }
  return fieldOverrides;
};
export const mergeGridModuleBodyHelper = (state, id, partialBody
// { moduleDefaultType }
) => {
  const tree = findGridTreeWithModuleId(state, id);
  // const isDefaultImageModule = moduleDefaultType === 'linked_image';

  if (tree) {
    // state = copyLayoutSectionFromSchemaToContentIfNeeded(
    //   state,
    //   tree.getRootName()
    // );

    // Make sure to get cell reference in newly modified tree
    let newTree = state.getIn(['gridLayoutTrees', tree.getRootId()]);
    const newPartialBody = injectPathIntoFieldOverridesIfMissing(state, tree.getRootId(), id, partialBody);
    const fieldOverrides = Object.entries(newPartialBody || {}).reduce((acc, [key, value]) => {
      acc[key] = convertJsonToFieldValueAvroWithoutTypename(value);
      return acc;
    }, {});
    ({
      tree: newTree
    } = newTree.mergeModuleFieldOverrides(id, fieldOverrides, {
      skipBuiltInChirpConversions: true
    }));

    // if (
    //   isDefaultImageModule &&
    //   cell.hasValue() &&
    //   cell.getValue().merge_in_default_values == null
    // ) {
    //   ({ tree: newTree } = newTree.mergeIntoCellValue(cell.getName(), {
    //     merge_in_default_values: false,
    //   }));
    // }

    return state.setIn(['gridLayoutTrees', tree.getRootId()], newTree);
  }

  // const moduleKey = getKeyForNonLayoutSection(state, id);
  // if (!moduleKey) {
  //   logUnknownModuleToRaven(id);
  //   return state;
  // }
  // const moduleBodyKey = [...moduleKey, 'body'];
  // const modulemergeInDefaultFieldValuesKey = [
  //   ...moduleKey,
  //   'merge_in_default_values',
  // ];
  // state = state.mergeIn(moduleBodyKey, fromJS(partialBody));

  // if (
  //   isDefaultImageModule &&
  //   !state.hasIn(modulemergeInDefaultFieldValuesKey)
  // ) {
  //   state = state.setIn(modulemergeInDefaultFieldValuesKey, false);
  // }

  return state;
};

// This is used to change top level data fields that we use like "Label" on a module
export const mergeModuleDataHelper = (state, id, partialData, layoutSectionId) => {
  const tree = findTreeWithCellName(state, id);
  if (tree) {
    state = copyLayoutSectionFromSchemaToContentIfNeeded(state, tree.getRootName());

    // Make sure to get cell reference in newly modified tree
    let newTree = state.getIn(['layoutSectionTrees', tree.getRootName()]);
    const cell = tree.findCell(id);
    ({
      tree: newTree
    } = newTree.mergeIntoCellValue(cell.getName(), partialData));
    const layoutSectionWidgets = state.getIn(['layoutSectionWidgets', layoutSectionId]);
    const widgetIndex = layoutSectionWidgets.findIndex(widget => widget.get('name') === id);
    const newLayoutSectionWidgets = layoutSectionWidgets.setIn([widgetIndex, 'label'], partialData.label);
    state = state.setIn(['layoutSectionWidgets', layoutSectionId], newLayoutSectionWidgets);
    return state.setIn(['layoutSectionTrees', tree.getRootName()], newTree);
  }
  const moduleKey = getKeyForNonLayoutSection(state, id);
  if (!moduleKey) {
    logUnknownModuleToRaven(id);
    return state;
  }
  state = state.mergeIn(moduleKey, fromJS(partialData));
  return state;
};
export const mergeLayoutFragmentDataHelper = (state, id, partialData, isRow) => {
  const tree = isRow ? findTreeWithRowName(state, id) : findTreeWithCellName(state, id);
  state = copyLayoutSectionFromSchemaToContentIfNeeded(state, tree.getRootName());

  // Make sure to get cell reference in newly modified tree
  let newTree = state.getIn(['layoutSectionTrees', tree.getRootName()]);
  if (isRow) {
    const row = tree.findRow(id);
    ({
      tree: newTree
    } = newTree.mergeIntoRowValue(row.getName(), partialData));
  } else {
    const cell = tree.findCell(id);
    ({
      tree: newTree
    } = newTree.mergeIntoCellValue(cell.getName(), partialData));
  }
  return state.setIn(['layoutSectionTrees', tree.getRootName()], newTree);
};
export const updateGridNodeLabelHelper = (state, id, partialData, gridLayoutId, isModule) => {
  const tree = findGridTreeWithModuleId(state, id);
  if (!tree) {
    return state;
  }
  let newTree = state.getIn(['gridLayoutTrees', gridLayoutId]);
  const node = tree.findNode(id);
  if (node.isSection()) {
    ({
      tree: newTree
    } = newTree.mergeSectionValue(id, partialData));
  } else if (node.isContainer()) {
    ({
      tree: newTree
    } = newTree.mergeContainerValue(id, partialData));
  } else if (node.isModule()) {
    ({
      tree: newTree
    } = newTree.mergeModuleValue(id, partialData));
  }
  state = state.setIn(['gridLayoutTrees', gridLayoutId], newTree);

  // Also update the gridLayoutModules array if this is a module
  if (isModule && partialData.label !== undefined) {
    const gridLayoutModules = state.getIn(['gridLayoutModules', gridLayoutId]);
    if (gridLayoutModules) {
      const moduleIndex = gridLayoutModules.findIndex(module => module.get('name') === id);
      if (moduleIndex !== -1) {
        state = state.setIn(['gridLayoutModules', gridLayoutId, moduleIndex, 'label'], partialData.label);
      }
    }
  }
  return state;
};
export const normalizeFlexColumnData = (contentFlexColumns, schemaFlexColumns = {}) => {
  if (contentFlexColumns) {
    const normalizedFlexColumns = {};
    const seen = {};

    // Ensure we keep the first instance of a named widget to keep in sync with the renderer,
    // so process each flex column in the render order
    const flexColumnIdsInOrder = Object.keys(schemaFlexColumns);
    flexColumnIdsInOrder.sort((a, b) => {
      const orderA = schemaFlexColumns[a] ? schemaFlexColumns[a].order : 0;
      const orderB = schemaFlexColumns[a] ? schemaFlexColumns[b].order : 0;
      return orderA - orderB;
    });
    flexColumnIdsInOrder.forEach(flexColumnId => {
      const flexColumn = contentFlexColumns[flexColumnId];
      if (flexColumn) {
        if (flexColumn.widgets) {
          const modules = [];
          flexColumn.widgets.forEach(module => {
            // Filter out any duplicate flex column widgets
            if (!seen[module.id]) {
              seen[module.id] = true;

              // In some cases, inside of a flex column, there can be a "nested" body object.
              // The renderer prefers the data in the nested body, so this attempts to replicate that behavior
              // We do it here instead of the selector because otherwise updates to the top level body would be
              // continually overwritten since the nested body takes precedence
              if (module.body && module.body.body && typeof module.body.body === 'object') {
                module.body = Object.assign({}, module.body, module.body.body);
                delete module.body.body;
              }

              // In some cases modules with smart content in a flex column will only have the
              // smart_objects list inside its body but not on the module itself. If that is the case
              // then this will copy over the body's smart_objects to the module.
              if (module.body && module.body.smart_objects && Array.isArray(module.body.smart_objects) && !module.smart_objects) {
                module.smart_objects = [...module.body.smart_objects];
              }
              modules.push(module);
            }
          });
          flexColumn.widgets = modules;
        }
        normalizedFlexColumns[flexColumnId] = flexColumn;
      }
    });
    return normalizedFlexColumns;
  }
  return contentFlexColumns;
};
export const maybeParseFlexAreaTree = ({
  content,
  templateContainsFlexArea
}) => {
  let flexAreaTree = ImmutableMap();
  if (templateContainsFlexArea) {
    const flexAreaData = parseFlexAreaDataForLayoutTree(content.flexAreas, content.widgets);
    createNodeNameQueueForFlexAreaTree();
    flexAreaTree = flexAreaTree.set(DND_AREA_ID, importTreeFromLayoutDataApi(flexAreaData, {
      shouldPreventEmptyRows: false,
      CellClass: CustomCellTweakingAutoDeleteLogicPerTestFlag,
      nodeNameQueueWriter: addToFlexAreaNodeNameQueue
    }));
  }
  return flexAreaTree;
};
export function setInsertedModulesFromRichTextImmutable(state, modulesInRichText) {
  state = state.merge({
    widgetsInRichText: state.get('widgetsInRichText').merge(modulesInRichText)
  });
  return state;
}
export function deleteInsertedModulesFromRichTextImmutable(state, removedModules) {
  let draft = state.get('widgetsInRichText');
  removedModules.forEach(widget => {
    draft = draft.delete(widget.name);
  });
  state = state.merge({
    widgetsInRichText: draft
  });
  return state;
}
export const getIndexedModuleInstances = newModuleInstances => {
  return newModuleInstances.reduce((instanceMap, moduleInstance) => {
    instanceMap[moduleInstance.name] = moduleInstance;
    return instanceMap;
  }, {});
};